import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, Validators } from '@angular/forms';
import {
  Attendance,
  DetailedRecurrenceFrequency,
  Gender,
  Language,
  OrderPopulatedResponseDto,
  ServiceSettlementType,
} from '@pflegix/types';
import { pairwise, tap } from 'rxjs';
import { getFrequencyFromVolumes } from '../../util';
import { canDefineFrequency } from '../../util/can-define-frequency.util';
import { OrderFormGroup, OrderFormGroupValue } from '../type/order-form-group.type';

@Injectable()
export class OrderFormService {
  private readonly formBuilder = inject(FormBuilder);

  public form: OrderFormGroup = this.formBuilder.group({
    settlementType: this.formBuilder.nonNullable.control<ServiceSettlementType>(ServiceSettlementType.SGB_XI_S45B_CARE),
    attendances: this.formBuilder.nonNullable.control<Attendance[]>([], Validators.required),
    period: this.formBuilder.nonNullable.group({
      startAt: this.formBuilder.nonNullable.control<Date>(new Date()),
      endAt: this.formBuilder.control<Date | null>(null),
    }),
    organization: this.formBuilder.nonNullable.control<string>('', Validators.required),
    caregiver: this.formBuilder.nonNullable.control<string>({ value: '', disabled: true }),
    languagePreference: this.formBuilder.nonNullable.control<Language[]>([]),
    hasGenderPreference: this.formBuilder.nonNullable.control<boolean>({ value: false, disabled: true }),
    genderPreference: this.formBuilder.nonNullable.control<Gender | undefined>({ disabled: true, value: undefined }),
    hasPetPreference: this.formBuilder.nonNullable.control<boolean>({ value: false, disabled: true }),
    pets: this.formBuilder.nonNullable.control<string>({ disabled: true, value: '' }),
    frequency: this.formBuilder.nonNullable.group({
      rhythm: this.formBuilder.nonNullable.control<DetailedRecurrenceFrequency | undefined>(
        undefined,
        Validators.required
      ),
      visits: this.formBuilder.nonNullable.control<number | undefined>(undefined, Validators.required),
      duration: this.formBuilder.nonNullable.control<number | undefined>(undefined, Validators.required),
    }),
    notes: this.formBuilder.nonNullable.control<string>(''),
  });

  constructor() {
    this.form.controls.frequency.disable();
    this.handleHasGenderPreferenceChanges();
    this.handleHasPetPreferenceChanges();
    this.handleOrganizationChanges();
    this.handleAttendancesChanges();
    this.handleFrequencyChanges();
  }

  public prefill(order: OrderPopulatedResponseDto): void {
    const formValue = this.mapOrderToFormValue(order);

    this.form.reset(formValue);
    this.form.markAsPristine();
  }

  public reset(order: OrderPopulatedResponseDto | undefined): void {
    const formValue = order ? this.mapOrderToFormValue(order) : {};

    this.form.reset(formValue);
  }

  private handleHasGenderPreferenceChanges(): void {
    const hasGenderPreferenceControl = this.form.controls.hasGenderPreference;
    const genderPreferenceControl = this.form.controls.genderPreference;

    hasGenderPreferenceControl.valueChanges
      .pipe(
        takeUntilDestroyed(),
        tap((hasGenderPreference) => {
          if (hasGenderPreference) {
            genderPreferenceControl.enable();
          } else {
            genderPreferenceControl.disable();
          }
        })
      )
      .subscribe();

    hasGenderPreferenceControl.statusChanges
      .pipe(
        takeUntilDestroyed(),
        pairwise(),
        tap(([prevStatus, nextStatus]) => {
          if (nextStatus === 'DISABLED') {
            genderPreferenceControl.disable();
          }

          if (prevStatus !== nextStatus && nextStatus !== 'DISABLED' && hasGenderPreferenceControl.value) {
            genderPreferenceControl.enable();
          }
        })
      )
      .subscribe();
  }

  private handleHasPetPreferenceChanges(): void {
    const hasPetPreferenceControl = this.form.controls.hasPetPreference;
    const petsControl = this.form.controls.pets;

    hasPetPreferenceControl.valueChanges
      .pipe(
        takeUntilDestroyed(),
        tap((hasPetPreference) => {
          if (hasPetPreference) {
            petsControl.enable();
          } else {
            petsControl.disable();
          }
        })
      )
      .subscribe();

    hasPetPreferenceControl.statusChanges
      .pipe(
        takeUntilDestroyed(),
        pairwise(),
        tap(([prevStatus, nextStatus]) => {
          if (nextStatus === 'DISABLED') {
            petsControl.disable();
          }

          if (prevStatus !== nextStatus && nextStatus !== 'DISABLED' && hasPetPreferenceControl.value) {
            petsControl.enable();
          }
        })
      )
      .subscribe();
  }

  private handleOrganizationChanges(): void {
    const caregiverControl = this.form.controls.caregiver;

    this.form.controls.organization.valueChanges
      .pipe(
        takeUntilDestroyed(),
        tap((selectedOrganization) => {
          if (selectedOrganization) {
            caregiverControl.enable();
            caregiverControl.reset('');
          } else {
            caregiverControl.disable();
          }
        })
      )
      .subscribe();
  }

  private handleAttendancesChanges(): void {
    const hasGenderPreferenceControl = this.form.controls.hasGenderPreference;
    const hasPetPreferenceControl = this.form.controls.hasPetPreference;
    const frequencyControl = this.form.controls.frequency;

    this.form.controls.attendances.valueChanges
      .pipe(
        takeUntilDestroyed(),
        tap((attendances) => {
          if (attendances && canDefineFrequency(attendances)) {
            hasGenderPreferenceControl.enable();
            hasPetPreferenceControl.enable();
            frequencyControl.enable();
          } else {
            hasGenderPreferenceControl.disable();
            hasPetPreferenceControl.disable();
            frequencyControl.disable();
          }
        })
      )
      .subscribe();
  }

  private handleFrequencyChanges(): void {
    this.form.controls.frequency.valueChanges
      .pipe(
        takeUntilDestroyed(),
        tap(() => {
          this.form.controls.frequency.controls.rhythm.markAsDirty();
          this.form.controls.frequency.controls.visits.markAsDirty();
          this.form.controls.frequency.controls.duration.markAsDirty();
        })
      )
      .subscribe();
  }

  private mapOrderToFormValue(order: OrderPopulatedResponseDto): OrderFormGroupValue {
    const startAt = order.services.care?.period.startAt;
    const endAt = order.services.care?.period.endAt;
    const pets = order.services.care?.additionalServices?.pets?.petType;

    const formValue: OrderFormGroupValue = {
      settlementType: order.services.care?.settlementType,
      attendances: order.services.care?.attendances,
      period: {
        startAt: startAt ? new Date(startAt) : undefined,
        endAt: endAt ? new Date(endAt) : undefined,
      },
      organization: order.services.care?.partners.organization?._id,
      caregiver: order.services.care?.partners.caregiver?._id,
      languagePreference: order.services.care?.languagePreference,
      hasGenderPreference:
        !!order.services.care?.genderPreference?.length && order.services.care?.genderPreference?.length > 0,
      genderPreference: order.services.care?.genderPreference?.[0],
      hasPetPreference: order.services.care?.additionalServices?.pets?.isPetCareRequested,
      pets: pets ?? '',
      frequency: getFrequencyFromVolumes(order.services.care?.volume),
      notes: order.partners.dependent.notes,
    };

    return formValue;
  }
}
